import * as THREE from 'three'
import { registerGSplatChunks } from '@/plugins/gsplat/shaders/gsplat-chunks'
import { fragmentShader, vertexShader } from '@/plugins/gsplat/shaders/gsplatCopyToWorkBuffer.glsl'
import { createGSplatModifyChunk } from '@/plugins/gsplat/shaders/gsplatModifyChunk'
import type {
  ExtendedWebGLRenderer,
  MutableDataTexture,
  TextureProperties,
} from '@/plugins/gsplat/types/WebGLTypes'
import { createUploadStream, type IUploadStream } from '@/plugins/gsplat/utils/UploadStream'
import { getLoggerManager } from '@/shared/logging'
import type { CompiledGSplatWorkBufferModifier } from '@/shared/types/gsplat'
import type { GSplatInfo } from './GSplatInfo'
import { GSplatWorkBufferRenderPass } from './GSplatWorkBufferRenderPass'
import { QuadRender } from './QuadRender'

const log = getLoggerManager().getLogger('gsplat:lod')

/**
 * 标量过滤模式枚举
 */
export enum GSplatScalarMode {
  /** 禁用 */
  None = 0,
  /** 体积（scale.x * scale.y * scale.z） */
  Volume = 1,
  /** 表面积近似（scale.x² + scale.y² + scale.z²） */
  SurfaceArea = 2,
  /** 明度（ITU-R BT.601: 0.299*r + 0.587*g + 0.114*b） */
  Value = 3,
}

/**
 * GSplat WorkBuffer 纹理格式配置
 *
 * PlayCanvas → Three.js 纹理格式映射：
 * - PIXELFORMAT_RGBA16F → RGBA + HalfFloatType
 * - RGBA8 fallback → RGBA + UnsignedByteType
 * - PIXELFORMAT_RGBA32U → RGBAIntegerFormat + UnsignedIntType
 * - PIXELFORMAT_RG32U → RGIntegerFormat + UnsignedIntType
 * - PIXELFORMAT_R32U → RedIntegerFormat + UnsignedIntType
 */
interface TextureFormatConfig {
  format: THREE.PixelFormat
  type: THREE.TextureDataType
  internalFormat?: THREE.PixelFormatGPU
}

interface ColorTextureFormatConfig extends TextureFormatConfig {
  internalFormat: 'RGBA16F' | 'RGBA8'
  label: 'rgba16f' | 'rgba8'
}

const TEXTURE_FORMATS: {
  COLOR_HDR: ColorTextureFormatConfig
  COLOR_FALLBACK: ColorTextureFormatConfig
  SPLAT0: TextureFormatConfig
  SPLAT1: TextureFormatConfig
  ORDER: TextureFormatConfig
} = {
  /** 颜色纹理（优先使用浮点，降级到无符号整数） */
  COLOR_HDR: {
    format: THREE.RGBAFormat,
    type: THREE.HalfFloatType,
    internalFormat: 'RGBA16F',
    label: 'rgba16f',
  },
  COLOR_FALLBACK: {
    format: THREE.RGBAFormat,
    type: THREE.UnsignedByteType,
    internalFormat: 'RGBA8',
    label: 'rgba8',
  },

  /** 几何纹理0：中心位置 + 协方差（RGBA32U） */
  SPLAT0: {
    format: THREE.RGBAIntegerFormat,
    type: THREE.UnsignedIntType,
    internalFormat: 'RGBA32UI',
  },

  /** 几何纹理1：协方差（RG32U） */
  SPLAT1: {
    format: THREE.RGIntegerFormat,
    type: THREE.UnsignedIntType,
    internalFormat: 'RG32UI',
  },

  /** 排序索引纹理（R32U） */
  ORDER: {
    format: THREE.RedIntegerFormat,
    type: THREE.UnsignedIntType,
    internalFormat: 'R32UI',
  },
}

type WorkBufferColorTextureFormat =
  | typeof TEXTURE_FORMATS.COLOR_HDR
  | typeof TEXTURE_FORMATS.COLOR_FALLBACK

/**
 * Work Buffer 的核心工作缓冲区
 *
 * 负责将多个 GSplatInfo 的数据渲染到 GPU 纹理中，供后续渲染使用。
 * 使用 MRT（多渲染目标）技术同时写入多个纹理。
 *
 * @remarks
 * 核心功能：
 * 1. **MRT 渲染**：同时写入颜色、几何、排序数据到不同纹理
 * 2. **双模式渲染**：
 *    - 完整更新（render）：更新所有纹理
 *    - 仅颜色更新（renderColor）：仅更新颜色纹理（用于 SH 优化）
 * 3. **动态调整**：支持纹理大小动态调整
 * 4. **排序数据注入**：从外部排序器接收排序索引
 *
 * 纹理布局：
 * - colorTexture (RGBA16F): [R, G, B, Alpha]
 * - splatTexture0 (RGBA32U): [center.x, center.y, center.z, pack(rotation.yz)]
 * - splatTexture1 (RG32U): [pack(rotation.w, scale.x), pack(scale.yz)]
 * - orderTexture (R32U): [sortedIndex]
 */

/** 渲染通道模式 */
export type PassMode = 'full' | 'colorOnly' | 'scalarOnly'

export interface GSplatWorkBufferOrderUploadResult {
  synchronousCopy: boolean
  conservativeCopy: boolean
  copiedBytes: number
  textureRecreated: boolean
  textureSizeChanged: boolean
  mode?: 'full' | 'chunked' | 'fallback'
}

export interface GSplatWorkBufferBeginOrderStagingOptions {
  textureSize: number
  visibleCount: number
  generation: number
}

export interface GSplatWorkBufferAdvanceOrderStagingOptions {
  maxBytesPerFrame: number
  maxChunksPerFrame: number
}

export interface GSplatWorkBufferAdvanceOrderStagingResult {
  completed: boolean
  submittedBytes: number
  submittedChunks: number
  remainingBytes: number
  remainingChunks: number
  blockedReason: 'byte-budget' | 'chunk-budget' | 'invalid-budget' | null
  minRowBudgetOverride: boolean
  fallbackToFullUpload: boolean
  fallbackReason: string | null
}

export interface GSplatWorkBufferOrderStagingInfo {
  generation: number
  textureSize: number
  visibleCount: number
  totalBytes: number
  submittedBytes: number
  remainingBytes: number
  submittedChunks: number
  remainingChunks: number
  uploadedElements: number
  totalUploadElements: number
}

interface OrderStagingJob {
  generation: number
  textureSize: number
  visibleCount: number
  orderData: Uint32Array
  totalUploadElements: number
  uploadedElements: number
  submittedBytes: number
  submittedChunks: number
}

/**
 * WorkBuffer 渲染信息（缓存 Shader 和 QuadRender）
 *
 * @remarks
 * 对应 PlayCanvas 的 WorkBufferRenderInfo 类
 * 每种配置（hasIntervals, passMode, shBands, isSogs）缓存一个实例
 */
class WorkBufferRenderInfo {
  material: THREE.ShaderMaterial
  quadRender: QuadRender
  isSogs: boolean
  private precompileStarted = false

  constructor(
    _renderer: THREE.WebGLRenderer,
    _hasIntervals: boolean,
    colorTextureFormat: WorkBufferColorTextureFormat,
    passMode: PassMode,
    shBands: number,
    isSogs: boolean = false,
    modifier: CompiledGSplatWorkBufferModifier | null = null
  ) {
    this.isSogs = isSogs

    // 根据参数构建 shader defines
    const defines: Record<string, string> = {}
    defines.GSPLAT_COPY_SEGMENTS = ''

    if (passMode === 'colorOnly') {
      defines.GSPLAT_COLOR_ONLY = ''
    } else if (passMode === 'scalarOnly') {
      defines.GSPLAT_SCALAR_ONLY = ''
    }

    // SOGS 格式标记
    if (isSogs) {
      defines.GSPLAT_SOGS_DATA = ''
    }

    // 颜色 fallback 保持 normalized sampler2D/out vec4 路径，不启用整数颜色分支。
    void colorTextureFormat

    // SH bands（从 resource 获取）
    defines.SH_BANDS = shBands.toString()

    // 创建 uniforms（根据格式选择不同的纹理 uniforms）
    const uniforms: Record<string, THREE.IUniform> = {
      uStartLine: { value: 0 },
      uViewportWidth: { value: 1 },
      uActiveSplats: { value: 0 },
      uTargetSize: { value: new THREE.Vector2(1, 1) },
      uColorMultiply: { value: new THREE.Vector3(1, 1, 1) },
      matrix_view: { value: new THREE.Matrix4() },
      uSegmentSourceBase: { value: 0 },
      uSegmentTargetOffset: { value: 0 },
      uSegmentCount: { value: 0 },
      uSegmentX: { value: 0 },
      uSegmentY: { value: 0 },
      uSegmentWidth: { value: 1 },
    }

    // scalarOnly 模式需要 uScalarMode uniform
    if (passMode === 'scalarOnly') {
      uniforms.uScalarMode = { value: 0 }
      uniforms.uScalarFilterExempt = { value: false }
    }

    if (isSogs) {
      // SOGS 格式 uniforms
      uniforms.splatMeansLow = { value: null }
      uniforms.splatMeansHigh = { value: null }
      uniforms.splatQuats = { value: null }
      uniforms.splatScales = { value: null }
      uniforms.splatSH0 = { value: null }
      uniforms.sogsMeans_mins = { value: new THREE.Vector3() }
      uniforms.sogsMeans_maxs = { value: new THREE.Vector3() }
      uniforms.sogsScales_mins = { value: new THREE.Vector3() }
      uniforms.sogsScales_maxs = { value: new THREE.Vector3() }
      uniforms.sogsSH0_mins = { value: new THREE.Vector4() }
      uniforms.sogsSH0_maxs = { value: new THREE.Vector4() }
      uniforms.sogsScalesCodebook = { value: null }
      uniforms.sogsSH0Codebook = { value: null }

      // SH 高阶纹理（如果有）
      if (shBands > 0) {
        uniforms.packedShN = { value: null }
        uniforms.sogsSH_labels = { value: null }
        uniforms.sogsSHN_mins = { value: 0 }
        uniforms.sogsSHN_maxs = { value: 0 }
        uniforms.sogsSHNCodebook = { value: null }
      }
    } else {
      // PLY 格式 uniforms
      uniforms.splatColor = { value: null }
      uniforms.splatDataA = { value: null }
      uniforms.splatDataB = { value: null }

      // SH 纹理（根据 SH_BANDS 添加）
      const uniformShBands = parseInt(defines.SH_BANDS || '0', 10)
      if (uniformShBands > 0) {
        uniforms.splatSH0 = { value: null }
        if (uniformShBands >= 2) {
          uniforms.splatSH1 = { value: null }
        }
        if (uniformShBands >= 3) {
          uniforms.splatSH2 = { value: null }
        }
      }
    }

    // 使用 chunk 管理器展开 #include 指令
    const manager = registerGSplatChunks()
    const chunkOverrides = modifier
      ? { gsplatModifyVS: createGSplatModifyChunk(modifier.code) }
      : undefined
    const processedFS = manager.processIncludes(fragmentShader, new Set(), chunkOverrides)

    this.material = new THREE.ShaderMaterial({
      vertexShader: vertexShader,
      fragmentShader: processedFS,
      defines: defines,
      uniforms: uniforms,
      glslVersion: THREE.GLSL3,
      depthTest: false,
      depthWrite: false,
      blending: THREE.NoBlending,
    })

    // 创建 QuadRender
    this.quadRender = new QuadRender(this.material)
  }

  precompile(renderer: THREE.WebGLRenderer): void {
    if (this.precompileStarted) {
      return
    }
    this.precompileStarted = true

    try {
      const result = this.quadRender.precompile(renderer)
      if (result && typeof (result as Promise<void>).catch === 'function') {
        void (result as Promise<void>).catch(() => {
          this.precompileStarted = false
        })
      }
    } catch {
      this.precompileStarted = false
    }
  }

  destroy(): void {
    this.quadRender.dispose()
    this.material.dispose()
  }
}

/**
 * GSplat WorkBuffer：聚合多资源到共享 RT，用于排序与最终渲染。
 */
export class GSplatWorkBuffer {
  /**
   * WebGL 渲染器
   */
  /* @internal */ private renderer: THREE.WebGLRenderer

  /**
   * 工作缓冲区纹理大小（宽度和高度相同）
   */
  private _textureSize: number = 1

  /**
   * 颜色纹理格式（根据硬件能力选择）
   */
  private colorTextureFormat!: WorkBufferColorTextureFormat

  private _renderable = true

  private _isWebGL2 = false

  private _hasColorBufferFloat = false

  private _hasColorBufferHalfFloat = false

  /**
   * UploadStream 实例（用于异步上传纹理数据）
   */
  private uploadStream: IUploadStream

  // ==================== 纹理 ====================

  /**
   * 颜色纹理（RGBA16F 或 RGBA16U）
   */
  colorTexture: THREE.DataTexture

  /**
   * 几何纹理0：中心位置 + 部分协方差（RGBA32U）
   */
  splatTexture0: THREE.DataTexture

  /**
   * 几何纹理1：协方差（RG32U）
   */
  splatTexture1: THREE.DataTexture

  /**
   * 排序索引纹理（R32U）
   */
  orderTexture: THREE.DataTexture

  private stagingOrderTexture: THREE.DataTexture | null = null

  private orderStagingJob: OrderStagingJob | null = null

  /**
   * 合并后的状态纹理（R8）
   * 用于中心点渲染模式，将所有 GSplatInfo 的状态合并到一个纹理
   */
  mergedStateTexture: THREE.DataTexture | null = null

  // ==================== 渲染目标 ====================

  /**
   * 多渲染目标（MRT）：同时写入颜色 + 几何数据
   * Three.js r150+ 使用 WebGLRenderTarget 的 count 选项
   */
  renderTarget: THREE.WebGLRenderTarget

  /**
   * 颜色渲染目标兼容别名。color-only pass 复用主 MRT，并临时只启用 attachment 0。
   */
  colorRenderTarget: THREE.WebGLRenderTarget

  /**
   * 标量渲染目标：仅写入标量纹理（用于 scalar filter）
   */
  scalarRenderTarget: THREE.WebGLRenderTarget | null = null

  // ==================== 渲染通道 ====================

  /**
   * 完整渲染通道（MRT）
   */
  private renderPass: GSplatWorkBufferRenderPass

  /**
   * 仅颜色渲染通道
   */
  private colorRenderPass: GSplatWorkBufferRenderPass

  /**
   * 标量渲染通道
   */
  private scalarRenderPass: GSplatWorkBufferRenderPass | null = null

  /**
   * WorkBufferRenderInfo 缓存
   * 键格式：`${hasIntervals}_${passMode}_${shBands}_${isSogs}`
   */
  private renderInfoCache: Map<string, WorkBufferRenderInfo> = new Map()

  // ==================== 标量过滤 ====================

  /**
   * 标量纹理（R32F，与 WorkBuffer 同布局）
   */
  scalarTexture: THREE.DataTexture | null = null

  /** 当前标量模式 */
  private _scalarMode: GSplatScalarMode = GSplatScalarMode.None

  /** 标量纹理是否需要更新 */
  private _scalarDirty = false

  /** 标量纹理版本号（每次更新递增） */
  private _scalarVersion = 0

  private readonly scalarFilteringSupported: boolean

  private warnedScalarFilteringUnsupported = false

  /**
   * 创建 GSplatWorkBuffer
   *
   * @param renderer - Three.js WebGL 渲染器
   */
  constructor(renderer: THREE.WebGLRenderer) {
    this.renderer = renderer

    // 检测 HDR 颜色格式支持
    this.detectColorFormat()
    this.scalarFilteringSupported = this.detectScalarFilteringSupport()

    // 创建纹理
    this.colorTexture = this.createTexture('splatColor', this.colorTextureFormat, 1, 1)
    this.splatTexture0 = this.createTexture('splatTexture0', TEXTURE_FORMATS.SPLAT0, 1, 1)
    this.splatTexture1 = this.createTexture('splatTexture1', TEXTURE_FORMATS.SPLAT1, 1, 1)
    this.orderTexture = this.createTexture('SplatGlobalOrder', TEXTURE_FORMATS.ORDER, 1, 1)

    // 创建 MRT 渲染目标（3个颜色附件）
    // Three.js r150+ 使用 count 参数
    this.renderTarget = new THREE.WebGLRenderTarget(1, 1, {
      count: 3,
      depthBuffer: false,
      stencilBuffer: false,
    })

    // 配置 MRT 纹理
    // 颜色纹理
    const colorTex = this.renderTarget.textures[0]
    colorTex.format = this.colorTextureFormat.format
    colorTex.type = this.colorTextureFormat.type
    colorTex.minFilter = THREE.NearestFilter
    colorTex.magFilter = THREE.NearestFilter
    colorTex.name = 'splatColor'
    // 关键修复：为颜色纹理设置正确的 internalFormat
    // RGBA16F (HalfFloatType) → 'RGBA16F'
    // RGBA8 (UnsignedByteType) → 'RGBA8'
    if (this.colorTextureFormat.type === THREE.HalfFloatType) {
      colorTex.internalFormat = 'RGBA16F'
    } else if (this.colorTextureFormat.type === THREE.UnsignedByteType) {
      colorTex.internalFormat = 'RGBA8'
    }

    // 几何纹理0 (RGBA32U)
    const splat0Tex = this.renderTarget.textures[1]
    splat0Tex.format = TEXTURE_FORMATS.SPLAT0.format
    splat0Tex.type = TEXTURE_FORMATS.SPLAT0.type
    splat0Tex.minFilter = THREE.NearestFilter
    splat0Tex.magFilter = THREE.NearestFilter
    splat0Tex.name = 'splatTexture0'
    splat0Tex.internalFormat = 'RGBA32UI'

    // 几何纹理1 (RG32U)
    const splat1Tex = this.renderTarget.textures[2]
    splat1Tex.format = TEXTURE_FORMATS.SPLAT1.format
    splat1Tex.type = TEXTURE_FORMATS.SPLAT1.type
    splat1Tex.minFilter = THREE.NearestFilter
    splat1Tex.magFilter = THREE.NearestFilter
    splat1Tex.name = 'splatTexture1'
    splat1Tex.internalFormat = 'RG32UI'

    // 保存引用（用于外部访问）
    this.colorTexture = this.renderTarget.textures[0] as THREE.DataTexture
    this.splatTexture0 = this.renderTarget.textures[1] as THREE.DataTexture
    this.splatTexture1 = this.renderTarget.textures[2] as THREE.DataTexture

    // color-only 必须写入最终渲染实际采样的 attachment 0。独立 RenderTarget 不仅多占一份
    // RGBA16F，还从未 copy 回主 colorTexture，导致颜色局部更新实际不可见。
    this.colorRenderTarget = this.renderTarget

    // 创建渲染通道
    this.initializeColorFormat()

    this.renderPass = new GSplatWorkBufferRenderPass(renderer, this, 'full')
    this.colorRenderPass = new GSplatWorkBufferRenderPass(renderer, this, 'colorOnly')

    // 创建 UploadStream 实例
    this.uploadStream = createUploadStream(renderer, { useSingleBuffer: true })
  }

  /**
   * 检测并选择最佳的颜色纹理格式
   *
   * @remarks
   * 优先使用 RGBA16F（HalfFloat），如果不支持则降级到 RGBA16U
   * PlayCanvas 的 device.getRenderableHdrFormat() 逻辑
   */
  private detectColorFormat(): void {
    const gl = this.renderer.getContext()
    this._isWebGL2 = this.isWebGL2Context(gl)
    this._hasColorBufferFloat = gl.getExtension('EXT_color_buffer_float') !== null
    this._hasColorBufferHalfFloat = gl.getExtension('EXT_color_buffer_half_float') !== null

    // 检查是否支持 Half Float 纹理渲染
    if (this._isWebGL2 && (this._hasColorBufferFloat || this._hasColorBufferHalfFloat)) {
      this.colorTextureFormat = TEXTURE_FORMATS.COLOR_HDR
    } else {
      // 降级到 RGBA8 normalized 颜色格式
      this.colorTextureFormat = TEXTURE_FORMATS.COLOR_FALLBACK
    }
  }

  private detectScalarFilteringSupport(): boolean {
    const gl = this.renderer.getContext()
    const isWebGL2 = this.isWebGL2Context(gl)
    const hasColorBufferFloat = gl.getExtension('EXT_color_buffer_float') !== null
    return isWebGL2 && hasColorBufferFloat
  }

  private isWebGL2Context(
    gl: WebGLRenderingContext | WebGL2RenderingContext
  ): gl is WebGL2RenderingContext {
    if (typeof WebGL2RenderingContext !== 'undefined' && gl instanceof WebGL2RenderingContext) {
      return true
    }
    const maybeWebGL2 = gl as WebGL2RenderingContext
    return (
      typeof maybeWebGL2.drawBuffers === 'function' &&
      typeof maybeWebGL2.texStorage2D === 'function'
    )
  }

  private initializeColorFormat(): void {
    const canTryHalfFloat =
      this._isWebGL2 && (this._hasColorBufferFloat || this._hasColorBufferHalfFloat)

    if (!this._isWebGL2) {
      this.colorTextureFormat = TEXTURE_FORMATS.COLOR_FALLBACK
      this.configureRenderTargetTextures(this.colorTextureFormat)
      this._renderable = false
      log.error('GSplatWorkBuffer: WebGL2 is required for Unified WorkBuffer, disabling WorkBuffer')
      return
    }

    if (canTryHalfFloat) {
      this.colorTextureFormat = TEXTURE_FORMATS.COLOR_HDR
      this.configureRenderTargetTextures(this.colorTextureFormat)
      if (this.validateRenderTargets('initial', 'warn')) {
        this._renderable = true
        return
      }
      log.warn('GSplatWorkBuffer: RGBA16F framebuffer incomplete, using RGBA8 fallback')
    } else {
      log.warn('GSplatWorkBuffer: HalfFloat render target unsupported, using RGBA8 fallback')
    }

    this.colorTextureFormat = TEXTURE_FORMATS.COLOR_FALLBACK
    this.renderTarget.dispose()
    this.configureRenderTargetTextures(this.colorTextureFormat)
    this._renderable = this.validateRenderTargets('initial-fallback', 'error')
    if (!this._renderable) {
      log.error('GSplatWorkBuffer: RGBA8 fallback framebuffer incomplete, disabling WorkBuffer')
    }
  }

  private configureRenderTargetTextures(formatConfig: WorkBufferColorTextureFormat): void {
    this.configureTexture(this.renderTarget.textures[0], 'splatColor', formatConfig)
    this.configureTexture(this.renderTarget.textures[1], 'splatTexture0', TEXTURE_FORMATS.SPLAT0)
    this.configureTexture(this.renderTarget.textures[2], 'splatTexture1', TEXTURE_FORMATS.SPLAT1)

    this.colorTexture = this.renderTarget.textures[0] as THREE.DataTexture
    this.splatTexture0 = this.renderTarget.textures[1] as THREE.DataTexture
    this.splatTexture1 = this.renderTarget.textures[2] as THREE.DataTexture
  }

  private configureTexture(
    texture: THREE.Texture,
    name: string,
    formatConfig: TextureFormatConfig
  ): void {
    texture.format = formatConfig.format
    texture.type = formatConfig.type
    texture.minFilter = THREE.NearestFilter
    texture.magFilter = THREE.NearestFilter
    texture.wrapS = THREE.ClampToEdgeWrapping
    texture.wrapT = THREE.ClampToEdgeWrapping
    texture.generateMipmaps = false
    texture.name = name
    texture.internalFormat = formatConfig.internalFormat ?? null
  }

  private validateRenderTargets(reason: string, level: 'warn' | 'error'): boolean {
    return this.validateRenderTarget(reason, 'full', this.renderTarget, level)
  }

  private validateRenderTarget(
    reason: string,
    pass: 'full' | 'colorOnly',
    target: THREE.WebGLRenderTarget,
    level: 'warn' | 'error'
  ): boolean {
    const gl = this.renderer.getContext()
    const previousTarget = this.renderer.getRenderTarget()
    let status = 0

    try {
      this.renderer.setRenderTarget(target)
      status = gl.checkFramebufferStatus(gl.FRAMEBUFFER)
    } finally {
      this.renderer.setRenderTarget(previousTarget)
    }

    if (status === gl.FRAMEBUFFER_COMPLETE) {
      return true
    }

    this.logFramebufferDiagnostic(reason, pass, target, status, level)
    return false
  }

  private logFramebufferDiagnostic(
    reason: string,
    pass: 'full' | 'colorOnly',
    target: THREE.WebGLRenderTarget,
    status: number,
    level: 'warn' | 'error'
  ): void {
    const gl = this.renderer.getContext()
    const colorTexture = pass === 'full' ? target.textures[0] : target.texture
    const splat0Internal = this.renderTarget.textures[1]?.internalFormat ?? 'none'
    const splat1Internal = this.renderTarget.textures[2]?.internalFormat ?? 'none'
    const gl2 = gl as WebGL2RenderingContext
    const maxDrawBuffers = gl.getParameter(gl2.MAX_DRAW_BUFFERS)
    const maxColorAttachments = gl.getParameter(gl2.MAX_COLOR_ATTACHMENTS)
    const message =
      `GSplatWorkBuffer framebuffer incomplete reason=${reason} pass=${pass}` +
      ` size=${target.width}x${target.height}` +
      ` colorFormat=${colorTexture.format} colorType=${colorTexture.type}` +
      ` colorInternal=${colorTexture.internalFormat ?? 'default'}` +
      ` splat0Internal=${splat0Internal} splat1Internal=${splat1Internal}` +
      ` isWebGL2=${this._isWebGL2}` +
      ` extColorBufferFloat=${this._hasColorBufferFloat}` +
      ` extColorBufferHalfFloat=${this._hasColorBufferHalfFloat}` +
      ` maxDrawBuffers=${maxDrawBuffers} maxColorAttachments=${maxColorAttachments}` +
      ` framebufferStatus=${status}`

    if (level === 'warn') {
      log.warn(message)
    } else {
      log.error(message)
    }
  }

  /**
   * 创建工作缓冲区纹理
   *
   * @param name - 纹理名称（用于调试）
   * @param formatConfig - 纹理格式配置
   * @param width - 宽度
   * @param height - 高度
   */
  private createTexture(
    name: string,
    formatConfig: TextureFormatConfig,
    width: number,
    height: number
  ): THREE.DataTexture {
    // 根据格式和类型创建对应的 TypedArray
    let data: Uint8Array | Uint16Array | Uint32Array | Float32Array
    const pixelCount = width * height

    if (formatConfig.type === THREE.HalfFloatType) {
      data = new Uint16Array(pixelCount * 4) // RGBA
    } else if (formatConfig.type === THREE.UnsignedByteType) {
      data = new Uint8Array(pixelCount * 4) // RGBA
    } else if (formatConfig.type === THREE.UnsignedIntType) {
      // 根据通道数分配
      const channels =
        formatConfig.format === THREE.RGBAIntegerFormat
          ? 4
          : formatConfig.format === THREE.RGIntegerFormat
            ? 2
            : 1
      data = new Uint32Array(pixelCount * channels)
    } else {
      data = new Float32Array(pixelCount * 4)
    }

    const texture = new THREE.DataTexture(
      data,
      width,
      height,
      formatConfig.format,
      formatConfig.type
    )

    texture.name = name
    texture.minFilter = THREE.NearestFilter
    texture.magFilter = THREE.NearestFilter
    texture.wrapS = THREE.ClampToEdgeWrapping
    texture.wrapT = THREE.ClampToEdgeWrapping
    texture.generateMipmaps = false

    // 为所有纹理明确设置 internalFormat（关键修复）
    if (formatConfig.internalFormat) {
      texture.internalFormat = formatConfig.internalFormat
    } else if (formatConfig.type === THREE.UnsignedIntType) {
      // 整数纹理
      if (formatConfig.format === THREE.RGBAIntegerFormat) {
        texture.internalFormat = 'RGBA32UI'
      } else if (formatConfig.format === THREE.RGIntegerFormat) {
        texture.internalFormat = 'RG32UI'
      } else if (formatConfig.format === THREE.RedIntegerFormat) {
        texture.internalFormat = 'R32UI'
      }
    } else if (formatConfig.type === THREE.HalfFloatType) {
      // 半精度浮点纹理
      if (formatConfig.format === THREE.RGBAFormat) {
        texture.internalFormat = 'RGBA16F'
      }
    } else if (formatConfig.type === THREE.UnsignedByteType) {
      // RGBA8 normalized 纹理（降级格式）
      if (formatConfig.format === THREE.RGBAFormat) {
        texture.internalFormat = 'RGBA8'
      }
    }

    texture.needsUpdate = true

    return texture
  }

  private createOrderTexture(name: string, size: number): THREE.DataTexture {
    return this.createTexture(name, TEXTURE_FORMATS.ORDER, size, size)
  }

  private resizeRenderTargets(newSize: number): void {
    this._textureSize = newSize
    this.renderTarget.setSize(newSize, newSize)

    if (this.scalarRenderTarget) {
      this.scalarRenderTarget.setSize(newSize, newSize)
    }

    const resizeDiagnosticLevel =
      this.colorTextureFormat === TEXTURE_FORMATS.COLOR_FALLBACK ? 'error' : 'warn'
    if (
      this._isWebGL2 &&
      this._renderable &&
      !this.validateRenderTargets('resize', resizeDiagnosticLevel)
    ) {
      if (this.colorTextureFormat !== TEXTURE_FORMATS.COLOR_FALLBACK) {
        log.warn('GSplatWorkBuffer: resize made RGBA16F incomplete, using RGBA8 fallback')
        this.colorTextureFormat = TEXTURE_FORMATS.COLOR_FALLBACK
        this.renderTarget.dispose()
        this.configureRenderTargetTextures(this.colorTextureFormat)
        this._renderable = this.validateRenderTargets('resize-fallback', 'error')
      } else {
        this._renderable = false
        log.error('GSplatWorkBuffer: RGBA8 fallback framebuffer incomplete, disabling WorkBuffer')
      }
    }
  }

  private ensureStagingOrderTexture(size: number): { textureRecreated: boolean } {
    let textureRecreated = false

    if (!this.stagingOrderTexture) {
      this.stagingOrderTexture = this.createOrderTexture('SplatGlobalOrderStaging', size)
      textureRecreated = true
    } else if (
      this.stagingOrderTexture.image.width !== size ||
      this.stagingOrderTexture.image.height !== size
    ) {
      this.resizeTexture(this.stagingOrderTexture, size, size)
      textureRecreated = true
    }

    return { textureRecreated }
  }

  /**
   * 获取当前纹理大小
   */
  get textureSize(): number {
    return this._textureSize
  }

  get renderable(): boolean {
    return this._renderable
  }

  /**
   * 调整工作缓冲区大小
   *
   * @param newSize - 新的纹理大小（宽度 = 高度）
   *
   * @remarks
   * 当 Splat 数量变化导致需要更大的纹理时调用
   * 会重新分配所有纹理和渲染目标
   */
  resize(newSize: number): void {
    if (newSize === this._textureSize) {
      return
    }

    this.abortOrderStaging()

    // 仅调整 Order 纹理（CPU 上传），其余 renderTarget 纹理由 setSize 驱动，无需手动分配 data
    this.resizeTexture(this.orderTexture, newSize, newSize)

    this.resizeRenderTargets(newSize)
  }

  /**
   * 调整单个纹理大小
   *
   * @remarks
   * 关键修复：调用 dispose() 后，必须重置纹理的 version 以强制 Three.js 重新创建 GPU 纹理
   * 否则 Three.js 可能尝试用 texSubImage2D 更新已删除的纹理，导致 WebGL 错误
   */
  private resizeTexture(texture: THREE.DataTexture, width: number, height: number): void {
    const pixelCount = width * height
    const oldData = texture.image.data

    // 根据现有数据类型重新分配
    let newData: Uint8Array | Uint16Array | Uint32Array | Float32Array

    if (oldData instanceof Uint8Array) {
      const channels = texture.format === THREE.RGBAFormat ? 4 : 1
      newData = new Uint8Array(pixelCount * channels)
    } else if (oldData instanceof Uint16Array) {
      const channels = texture.format === THREE.RGBAFormat ? 4 : 2
      newData = new Uint16Array(pixelCount * channels)
    } else if (oldData instanceof Uint32Array) {
      const channels =
        texture.format === THREE.RGBAIntegerFormat
          ? 4
          : texture.format === THREE.RGIntegerFormat
            ? 2
            : 1
      newData = new Uint32Array(pixelCount * channels)
    } else {
      newData = new Float32Array(pixelCount * 4)
    }

    // 先 dispose 旧的 WebGL 纹理资源，确保 GPU 纹理会重新分配
    texture.dispose()

    // 更新纹理
    texture.image = {
      data: newData,
      width: width,
      height: height,
    }

    // 关键修复：重置 version 为 0，强制 Three.js 完全重新创建纹理
    // 而不是尝试用 texSubImage2D 更新（会导致尺寸不匹配错误）
    ;(texture as MutableDataTexture).version = 0
    texture.needsUpdate = true
  }

  /**
   * 渲染所有 Splat 数据到工作缓冲区（完整更新）
   *
   * @param splats - 要渲染的 GSplatInfo 数组
   * @param cameraNode - 相机节点（用于 SH 计算）
   * @param colorsByLod - 每个 LOD 的调试颜色（可选）
   *
   * @remarks
   * 完整渲染包括：
   * 1. 颜色数据（含 SH 评估）
   * 2. 中心位置（asset-local）
   * 3. 协方差数据（asset-local）
   */
  render(splats: GSplatInfo[], cameraNode: THREE.Object3D, colorsByLod?: number[][]): void {
    if (!this._renderable) return
    if (this.renderPass.update(splats, cameraNode, colorsByLod)) {
      this.renderPass.render()
    }
  }

  /**
   * 仅渲染颜色数据到工作缓冲区（部分更新）
   *
   * @param splats - 要渲染的 GSplatInfo 数组
   * @param cameraNode - 相机节点（用于 SH 计算）
   * @param colorsByLod - 每个 LOD 的调试颜色（可选）
   *
   * @remarks
   * 用于优化 SH 颜色更新：
   * - 当相机移动但几何数据不变时，仅重新计算颜色
   * - 避免重复写入几何纹理，提高性能
   */
  renderColor(splats: GSplatInfo[], cameraNode: THREE.Object3D, colorsByLod?: number[][]): void {
    if (!this._renderable) return
    if (this.colorRenderPass.update(splats, cameraNode, colorsByLod)) {
      this.colorRenderPass.render()
    }
  }

  /**
   * 设置排序数据到 Order 纹理
   *
   * @param orderData - 排序后的索引数组（Uint32Array）
   *
   * @remarks
   * 由外部排序器（UnifiedSorter）调用，将排序结果注入 WorkBuffer
   * 使用 UploadStream 进行异步上传以提高性能
   */
  setOrderData(orderData: Uint32Array): GSplatWorkBufferOrderUploadResult {
    const expectedSize = this._textureSize * this._textureSize
    let textureSizeChanged = false
    let textureRecreated = false

    // 如果数据超过当前容量，自动扩容到最近的平方尺寸
    if (orderData.length > expectedSize) {
      const requiredSize = Math.ceil(Math.sqrt(orderData.length))
      this.resize(requiredSize)
      textureSizeChanged = true
      textureRecreated = true
    }

    // 重新计算容量（可能发生扩容）
    const texWidth = this.orderTexture.image.width
    const texHeight = this.orderTexture.image.height
    const texelCount = texWidth * texHeight
    const existingDataLen = (this.orderTexture.image.data as Uint32Array)?.length ?? 0

    // 检查纹理 image 大小是否与 texelCount 匹配
    if (existingDataLen !== texelCount) {
      // 需要 dispose 并重置 version，确保 Three.js 重新创建 GPU 纹理
      this.orderTexture.dispose()
      this.orderTexture.image.data = new Uint32Array(texelCount)
      ;(this.orderTexture as MutableDataTexture).version = 0
      textureRecreated = true
    }

    if (orderData.length > texelCount) {
      log.error(
        `[GSplatWorkBuffer] orderData length overflow length=${orderData.length} texelCount=${texelCount}, will clamp`
      )
    }

    // 复制数据到纹理
    const targetData = this.orderTexture.image.data as Uint32Array
    const copyLen = Math.min(orderData.length, texelCount)
    targetData.set(orderData.subarray(0, copyLen))

    // 如果 orderData 比纹理小，填充剩余部分为 0
    if (copyLen < texelCount) {
      targetData.fill(0, copyLen)
    }

    // 确保 image 尺寸和 data 长度一致
    this.orderTexture.image.width = texWidth
    this.orderTexture.image.height = texHeight

    // 检查是否需要强制重新创建 GPU 纹理
    // Three.js 内部通过 __webglTexture 追踪 GPU 纹理，如果 dispose() 后该属性会被清除
    // 但有时 dispose() 只是标记删除，需要确保纹理被完全重新创建
    const extRenderer = this.renderer as ExtendedWebGLRenderer
    const properties = extRenderer.properties?.get(this.orderTexture) as
      | TextureProperties
      | undefined
    if (properties?.__webglTexture) {
      // 已经有 GPU 纹理，但 CPU 尺寸可能已变化
      // 通过 dispose() 强制 Three.js 在下次使用时重新创建
      // 注意：这里不能用 getTexLevelParameter（Chrome 可能不支持）
      // 而是通过比较 __version 与 texture.version 来判断是否需要更新
      const currentVersion = (this.orderTexture as MutableDataTexture).version

      // 如果 version 已经被重置（resize 时设为 0），强制清理 GPU 纹理
      if (currentVersion === 0) {
        // dispose 会清理 __webglTexture
        this.orderTexture.dispose()
        textureRecreated = true
      }
    }

    // 标记需要更新
    const beforeUploadVersion = this.orderTexture.version
    try {
      this.uploadStream.uploadRegion(targetData, this.orderTexture, {
        sourceOffset: 0,
        targetOffset: 0,
        size: texelCount,
      })
      if (this.orderTexture.version <= beforeUploadVersion) {
        this.orderTexture.needsUpdate = true
      }
    } catch (error) {
      this.orderTexture.needsUpdate = true
      log.warn(
        '[GSplatWorkBuffer] Failed to stream order texture upload; falling back to Three texture upload: ' +
          (error instanceof Error ? error.message : String(error))
      )
    }

    return {
      synchronousCopy: true,
      conservativeCopy: false,
      copiedBytes: copyLen * Uint32Array.BYTES_PER_ELEMENT,
      textureRecreated,
      textureSizeChanged,
      mode: 'full',
    }
  }

  beginOrderStaging(
    orderData: Uint32Array,
    options: GSplatWorkBufferBeginOrderStagingOptions
  ): GSplatWorkBufferOrderStagingInfo {
    this.abortOrderStaging()

    const textureSize = Math.max(1, Math.floor(options.textureSize))
    this.ensureStagingOrderTexture(textureSize)

    const texelCount = textureSize * textureSize
    const visibleCount = Math.min(texelCount, Math.max(0, Math.floor(options.visibleCount)))
    const validElements = Math.min(visibleCount, orderData.length)
    const totalUploadElements =
      validElements === 0
        ? 0
        : Math.min(texelCount, Math.ceil(validElements / textureSize) * textureSize)

    this.orderStagingJob = {
      generation: Math.max(0, Math.floor(options.generation)),
      textureSize,
      visibleCount,
      orderData,
      totalUploadElements,
      uploadedElements: 0,
      submittedBytes: 0,
      submittedChunks: 0,
    }

    return this.getOrderStagingInfo()!
  }

  advanceOrderStaging(
    options: GSplatWorkBufferAdvanceOrderStagingOptions
  ): GSplatWorkBufferAdvanceOrderStagingResult {
    const job = this.orderStagingJob
    if (!job) {
      return {
        completed: true,
        submittedBytes: 0,
        submittedChunks: 0,
        remainingBytes: 0,
        remainingChunks: 0,
        blockedReason: null,
        minRowBudgetOverride: false,
        fallbackToFullUpload: false,
        fallbackReason: null,
      }
    }

    const width = job.textureSize
    const rowBytes = width * Uint32Array.BYTES_PER_ELEMENT
    const maxChunks = Math.floor(options.maxChunksPerFrame)
    const maxBytes = options.maxBytesPerFrame

    if (job.totalUploadElements === 0) {
      return this.createAdvanceResult(job, 0, 0, null, false, false, null)
    }

    if (
      maxChunks <= 0 ||
      typeof maxBytes !== 'number' ||
      Number.isNaN(maxBytes) ||
      (!Number.isFinite(maxBytes) && maxBytes !== Number.POSITIVE_INFINITY)
    ) {
      return this.createAdvanceResult(job, 0, 0, 'invalid-budget', false, true, 'invalid-budget')
    }

    if (maxBytes <= 0) {
      return this.createAdvanceResult(job, 0, 0, 'invalid-budget', false, true, 'invalid-budget')
    }

    let submittedBytes = 0
    let submittedChunks = 0
    let blockedReason: GSplatWorkBufferAdvanceOrderStagingResult['blockedReason'] = null
    let minRowBudgetOverride = false

    while (job.uploadedElements < job.totalUploadElements && submittedChunks < maxChunks) {
      const remainingElements = job.totalUploadElements - job.uploadedElements
      const remainingRows = Math.ceil(remainingElements / width)
      const remainingBudget =
        maxBytes === Number.POSITIVE_INFINITY ? Number.POSITIVE_INFINITY : maxBytes - submittedBytes

      if (remainingBudget < rowBytes) {
        if (submittedChunks > 0) {
          blockedReason = 'byte-budget'
          break
        }
        // 一行是 texSubImage2D 的最小安全粒度；只覆盖当前这一行。
        minRowBudgetOverride = true
      }

      const forceOneRow = remainingBudget < rowBytes
      const budgetRows =
        remainingBudget === Number.POSITIVE_INFINITY
          ? remainingRows
          : forceOneRow
            ? 1
            : Math.max(1, Math.floor(remainingBudget / rowBytes))
      const rowsToUpload = Math.max(1, Math.min(remainingRows, budgetRows))
      const chunkElements = rowsToUpload * width
      const targetOffset = job.uploadedElements

      try {
        this.uploadOrderStagingChunk(job, targetOffset, chunkElements)
      } catch (error) {
        const reason = error instanceof Error ? error.message : 'upload-error'
        return this.createAdvanceResult(
          job,
          submittedBytes,
          submittedChunks,
          null,
          minRowBudgetOverride,
          true,
          reason
        )
      }

      const chunkBytes = chunkElements * Uint32Array.BYTES_PER_ELEMENT
      job.uploadedElements += chunkElements
      job.submittedBytes += chunkBytes
      job.submittedChunks++
      submittedBytes += chunkBytes
      submittedChunks++
    }

    if (job.uploadedElements < job.totalUploadElements && submittedChunks >= maxChunks) {
      blockedReason = 'chunk-budget'
    }

    return this.createAdvanceResult(
      job,
      submittedBytes,
      submittedChunks,
      blockedReason,
      minRowBudgetOverride,
      false,
      null
    )
  }

  private uploadOrderStagingChunk(
    job: OrderStagingJob,
    targetOffset: number,
    chunkElements: number
  ): void {
    const stagingTexture = this.stagingOrderTexture
    if (!stagingTexture) {
      throw new Error('Missing staging order texture')
    }

    const stagingData = stagingTexture.image.data as Uint32Array
    const sourceEnd = Math.min(job.orderData.length, targetOffset + chunkElements)
    const sourceCount = Math.max(0, sourceEnd - targetOffset)

    if (sourceCount === chunkElements) {
      stagingData.set(
        job.orderData.subarray(targetOffset, targetOffset + chunkElements),
        targetOffset
      )
      this.uploadStream.uploadRegion(job.orderData, stagingTexture, {
        sourceOffset: targetOffset,
        targetOffset,
        size: chunkElements,
      })
      return
    }

    const scratch = new Uint32Array(chunkElements)
    if (sourceCount > 0) {
      scratch.set(job.orderData.subarray(targetOffset, sourceEnd))
      stagingData.set(job.orderData.subarray(targetOffset, sourceEnd), targetOffset)
    }
    if (sourceCount < chunkElements) {
      stagingData.fill(0, targetOffset + sourceCount, targetOffset + chunkElements)
    }

    this.uploadStream.uploadRegion(scratch, stagingTexture, {
      sourceOffset: 0,
      targetOffset,
      size: chunkElements,
    })
  }

  private createAdvanceResult(
    job: OrderStagingJob,
    submittedBytes: number,
    submittedChunks: number,
    blockedReason: GSplatWorkBufferAdvanceOrderStagingResult['blockedReason'],
    minRowBudgetOverride: boolean,
    fallbackToFullUpload: boolean,
    fallbackReason: string | null
  ): GSplatWorkBufferAdvanceOrderStagingResult {
    const remainingElements = Math.max(0, job.totalUploadElements - job.uploadedElements)
    return {
      completed: remainingElements === 0,
      submittedBytes,
      submittedChunks,
      remainingBytes: remainingElements * Uint32Array.BYTES_PER_ELEMENT,
      remainingChunks: Math.ceil(remainingElements / job.textureSize),
      blockedReason,
      minRowBudgetOverride,
      fallbackToFullUpload,
      fallbackReason,
    }
  }

  commitOrderStaging(): GSplatWorkBufferOrderUploadResult {
    const job = this.orderStagingJob
    const stagingTexture = this.stagingOrderTexture
    if (!job || !stagingTexture) {
      throw new Error('No staging order upload job to commit')
    }
    if (job.uploadedElements < job.totalUploadElements) {
      throw new Error('Cannot commit incomplete staging order upload')
    }

    const textureSizeChanged = job.textureSize !== this._textureSize
    const previousActive = this.orderTexture
    this.orderTexture = stagingTexture
    this.stagingOrderTexture = previousActive
    this.orderStagingJob = null

    if (textureSizeChanged) {
      this.resizeRenderTargets(job.textureSize)
    }

    return {
      synchronousCopy: false,
      conservativeCopy: false,
      copiedBytes: job.submittedBytes,
      textureRecreated: true,
      textureSizeChanged,
      mode: 'chunked',
    }
  }

  abortOrderStaging(): void {
    this.orderStagingJob = null
  }

  getOrderStagingInfo(): GSplatWorkBufferOrderStagingInfo | null {
    const job = this.orderStagingJob
    if (!job) {
      return null
    }
    const remainingElements = Math.max(0, job.totalUploadElements - job.uploadedElements)
    return {
      generation: job.generation,
      textureSize: job.textureSize,
      visibleCount: job.visibleCount,
      totalBytes: job.totalUploadElements * Uint32Array.BYTES_PER_ELEMENT,
      submittedBytes: job.submittedBytes,
      remainingBytes: remainingElements * Uint32Array.BYTES_PER_ELEMENT,
      submittedChunks: job.submittedChunks,
      remainingChunks: Math.ceil(remainingElements / job.textureSize),
      uploadedElements: job.uploadedElements,
      totalUploadElements: job.totalUploadElements,
    }
  }

  /**
   * 销毁所有资源
   */
  destroy(): void {
    // 销毁渲染通道
    this.renderPass.destroy()
    this.colorRenderPass.destroy()
    if (this.scalarRenderPass) {
      this.scalarRenderPass.destroy()
      this.scalarRenderPass = null
    }

    // 销毁缓存的 WorkBufferRenderInfo
    for (const renderInfo of this.renderInfoCache.values()) {
      renderInfo.destroy()
    }
    this.renderInfoCache.clear()

    // 销毁纹理
    this.colorTexture.dispose()
    this.splatTexture0.dispose()
    this.splatTexture1.dispose()
    this.disposeCpuBackedDataTexture(this.orderTexture)
    if (this.stagingOrderTexture && this.stagingOrderTexture !== this.orderTexture) {
      this.disposeCpuBackedDataTexture(this.stagingOrderTexture)
    }
    this.stagingOrderTexture = null
    this.orderStagingJob = null

    // 销毁标量纹理和渲染目标
    if (this.scalarTexture) {
      this.scalarTexture.dispose()
      this.scalarTexture = null
    }
    if (this.scalarRenderTarget) {
      this.scalarRenderTarget.dispose()
      this.scalarRenderTarget = null
    }

    // 销毁状态纹理
    if (this.mergedStateTexture) {
      this.disposeCpuBackedDataTexture(this.mergedStateTexture)
      this.mergedStateTexture = null
    }

    // 销毁渲染目标
    this.renderTarget.dispose()

    // 销毁 UploadStream
    this.uploadStream.destroy()
  }

  private disposeCpuBackedDataTexture(texture: THREE.DataTexture): void {
    try {
      texture.dispose()
    } finally {
      const baseTexture = texture as THREE.Texture
      baseTexture.image = null
    }
  }

  /**
   * 获取或创建 WorkBufferRenderInfo（缓存机制）
   *
   * @param hasIntervals - 是否有 LOD 区间
   * @param passMode - 渲染通道模式
   * @param shBands - SH 球谐级别 (0, 1, 2, 3)
   * @param isSogs - 是否为 SOGS 压缩格式
   * @returns 缓存的 WorkBufferRenderInfo 实例
   *
   * @internal 由 GSplatWorkBufferRenderPass 调用
   */
  getWorkBufferRenderInfo(
    hasIntervals: boolean,
    passMode: PassMode,
    shBands: number,
    isSogs: boolean = false,
    modifier: CompiledGSplatWorkBufferModifier | null = null
  ): WorkBufferRenderInfo {
    const modifierHash = modifier?.hash ?? 0
    const key = `${passMode}_${shBands}_${isSogs}_${modifierHash}`

    let renderInfo = this.renderInfoCache.get(key)
    if (!renderInfo) {
      renderInfo = new WorkBufferRenderInfo(
        this.renderer,
        hasIntervals,
        this.colorTextureFormat,
        passMode,
        shBands,
        isSogs,
        modifier
      )
      this.renderInfoCache.set(key, renderInfo)
    }

    return renderInfo
  }

  prewarmRenderInfo(
    passMode: PassMode,
    shBands: number,
    isSogs: boolean = false,
    modifier: CompiledGSplatWorkBufferModifier | null = null
  ): void {
    const renderInfo = this.getWorkBufferRenderInfo(false, passMode, shBands, isSogs, modifier)
    renderInfo.precompile(this.renderer)
  }

  // ==================== 标量过滤方法 ====================

  /**
   * 设置标量模式。mode 变化时会标记 dirty 并按需创建/释放相关资源。
   *
   * @param mode - 标量模式；传入 None 关闭过滤
   */
  setScalarMode(mode: GSplatScalarMode): void {
    if (this._scalarMode === mode) return
    this._scalarMode = mode

    if (mode === GSplatScalarMode.None) {
      this._scalarDirty = false
      return
    }

    this.ensureScalarResources()
    this._scalarDirty = this.scalarRenderTarget !== null
  }

  /**
   * 标记标量纹理需要更新
   */
  markScalarDirty(): void {
    if (this._scalarMode !== GSplatScalarMode.None && this.scalarFilteringSupported) {
      this._scalarDirty = true
    }
  }

  /**
   * 标量纹理版本号
   */
  get scalarVersion(): number {
    return this._scalarVersion
  }

  /**
   * 标量纹理是否就绪
   */
  get scalarTextureReady(): boolean {
    return this.scalarTexture !== null && this._scalarVersion > 0
  }

  /** 当前硬件是否支持标量过滤。 */
  get supportsScalarFiltering(): boolean {
    return this.scalarFilteringSupported
  }

  /**
   * 创建标量渲染所需的资源（惰性初始化）
   */
  private ensureScalarResources(): void {
    if (this.scalarRenderTarget) return
    if (!this.scalarFilteringSupported) {
      if (!this.warnedScalarFilteringUnsupported) {
        log.warn(
          'GSplatWorkBuffer: scalar filtering requires WebGL2 with EXT_color_buffer_float support'
        )
        this.warnedScalarFilteringUnsupported = true
      }
      return
    }

    const size = this._textureSize

    // 创建标量渲染目标（R32F 单通道浮点）
    this.scalarRenderTarget = new THREE.WebGLRenderTarget(size, size, {
      depthBuffer: false,
      stencilBuffer: false,
    })
    this.scalarRenderTarget.texture.format = THREE.RedFormat
    this.scalarRenderTarget.texture.type = THREE.FloatType
    this.scalarRenderTarget.texture.minFilter = THREE.NearestFilter
    this.scalarRenderTarget.texture.magFilter = THREE.NearestFilter
    this.scalarRenderTarget.texture.internalFormat = 'R32F'
    this.scalarRenderTarget.texture.name = 'splatScalar'

    // 使用 renderTarget 的 texture 作为 scalarTexture 引用
    this.scalarTexture = this.scalarRenderTarget.texture as THREE.DataTexture

    // 创建专用渲染通道
    this.scalarRenderPass = new GSplatWorkBufferRenderPass(this.renderer, this, 'scalarOnly')
  }

  /**
   * 渲染标量纹理（仅在 dirty 时执行）。
   *
   * @param splats - 当前帧参与的 GSplatInfo 列表
   * @param cameraNode - 相机对应的 Object3D，用于提供世界变换
   */
  renderScalar(splats: GSplatInfo[], cameraNode: THREE.Object3D): void {
    if (!this._renderable) return
    if (this._scalarMode === GSplatScalarMode.None || !this._scalarDirty) return
    if (!this.scalarRenderPass) return

    if (!this.scalarRenderPass.update(splats, cameraNode)) return

    this.scalarRenderPass.render()
    this._scalarDirty = false
    this._scalarVersion++
  }

  /**
   * 获取当前标量模式
   */
  get scalarMode(): GSplatScalarMode {
    return this._scalarMode
  }

  /**
   * 合并多个 GSplatInfo 的状态纹理到统一纹理
   *
   * @param splats - GSplatInfo 数组
   * @returns 合并后的状态纹理，如果没有状态数据则返回 null
   *
   * @remarks
   * 状态纹理布局与 WorkBuffer 的其他纹理一致：
   * - 每个 GSplatInfo 占用 [lineStart, lineStart + lineCount) 行
   * - 使用相同的 textureSize 宽度
   */
  mergeStateTextures(splats: GSplatInfo[]): THREE.DataTexture | null {
    // 检查是否有任何 splat 有状态数据
    const hasAnyState = splats.some((splat) => splat.stateData !== null)
    if (!hasAnyState) {
      // 没有状态数据，清理并返回 null
      if (this.mergedStateTexture) {
        this.mergedStateTexture.dispose()
        this.mergedStateTexture = null
      }
      return null
    }

    const texSize = this._textureSize
    const totalPixels = texSize * texSize

    // 创建或调整状态纹理大小
    if (!this.mergedStateTexture || this.mergedStateTexture.image.width !== texSize) {
      if (this.mergedStateTexture) {
        this.mergedStateTexture.dispose()
      }

      const stateData = new Uint8Array(totalPixels)
      this.mergedStateTexture = new THREE.DataTexture(
        stateData,
        texSize,
        texSize,
        THREE.RedFormat,
        THREE.UnsignedByteType
      )

      this.mergedStateTexture.name = 'mergedSplatState'
      this.mergedStateTexture.minFilter = THREE.NearestFilter
      this.mergedStateTexture.magFilter = THREE.NearestFilter
      this.mergedStateTexture.wrapS = THREE.ClampToEdgeWrapping
      this.mergedStateTexture.wrapT = THREE.ClampToEdgeWrapping
      this.mergedStateTexture.generateMipmaps = false
      this.mergedStateTexture.internalFormat = 'R8'
    }

    // 获取纹理数据缓冲区
    const mergedData = this.mergedStateTexture.image.data as Uint8Array

    // 清零
    mergedData.fill(0)

    // 合并每个 GSplatInfo 的状态数据
    for (const splat of splats) {
      const stateData = splat.stateData
      if (!stateData) continue

      // 计算在合并纹理中的起始位置
      const startOffset = splat.lineStart * texSize

      // 复制状态数据（考虑区间限制）
      const activeSplats = splat.activeSplats
      const copyLength = Math.min(stateData.length, activeSplats, totalPixels - startOffset)

      if (copyLength > 0 && startOffset >= 0 && startOffset < totalPixels) {
        // 直接复制（如果没有区间限制）或按区间重映射
        if (splat.intervals.length === 0) {
          // 无区间限制，直接复制
          for (let i = 0; i < copyLength; i++) {
            mergedData[startOffset + i] = stateData[i]
          }
        } else {
          // 有区间限制，需要按区间重映射
          let destIdx = 0
          for (let i = 0; i < splat.intervals.length; i += 2) {
            const intervalStart = splat.intervals[i]
            const intervalEnd = splat.intervals[i + 1]
            for (
              let srcIdx = intervalStart;
              srcIdx < intervalEnd && destIdx < copyLength;
              srcIdx++, destIdx++
            ) {
              if (srcIdx < stateData.length) {
                mergedData[startOffset + destIdx] = stateData[srcIdx]
              }
            }
          }
        }
      }
    }

    this.mergedStateTexture.needsUpdate = true
    return this.mergedStateTexture
  }
}
